// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setIsBadFile from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetIsBadFile";
import setUploadLimitExceeded from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetUploadLimitExceeded";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxProps } from "@insite/mobius/Checkbox";
import FileUpload from "@insite/mobius/FileUpload";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import importStoreList from "@store/Framework/Store/Components/StoreListUpload/Handlers/ImportStoreList";
import storeListProcessFile from "@store/Framework/Store/Components/StoreListUpload/Handlers/StoreListImportProcessFile";
import validateImportStoreList from "@store/Framework/Store/Components/StoreListUpload/Handlers/ValidateImportStoreList";
import updateStoreList from "@store/Framework/Store/Data/StoreLists/Handlers/UpdateStoreList";
import addStoreList from "@store/Framework/Store/Pages/StoreLists/Handlers/AddStoreList";
import { ImportStoreListUploadItems, StoreListModel } from "@store/Framework/Types/ApiModels";
import constants from "@toolkit/constants";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, useContext, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    storeList?: StoreListModel;
    onCancel: () => void;
    onSubmit: () => void;
    onImportError: (
        errors: ImportStoreListUploadItems[],
        canContinueImport: boolean,
        uploadedItems: ImportStoreListUploadItems[],
        storeListName: string,
        storeListDescription: string,
    ) => void;
    listNameErrorMessage?: string;
}

const mapStateToProps = (state: ApplicationState) => ({
    uploadedItems: state.store.components.storeListUpload.uploadedItems,
    uploadLimitExceeded: state.store.components.storeListUpload.uploadLimitExceeded,
    isBadFile: state.components.orderUpload.isBadFile,
});

const mapDispatchToProps = {
    addStoreList,
    updateStoreList,
    storeListProcessFile,
    setIsBadFile,
    setUploadLimitExceeded,
    importStoreList,
    validateImportStoreList,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const buttonStyles = () => {
    return css`
        ${breakpoint("xs", "only")} {
            width: 100%;
        }
    `;
};

export interface StoreListsImportListFormStyles {
    importTooltipText?: TypographyPresentationProps;
    importRowsLimitExceededText?: TypographyPresentationProps;
    downloadTemplateButton?: ButtonPresentationProps;
    fileGridItem?: GridItemProps;
    nameGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    buttonsGridItem?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    createButton?: ButtonPresentationProps;
    includeFirstRowCheckbox?: CheckboxProps;
    fileWrapper?: InjectableCss;
    importListFormWrapper?: InjectableCss;
}

export const storeListsImportListFormStyles: StoreListsImportListFormStyles = {
    importTooltipText: {
        css: css`
            margin-bottom: ${pxToRem(25)};
        `,
    },
    importRowsLimitExceededText: {
        css: css`
            margin-bottom: ${pxToRem(25)};
            color: ${getColor("danger")};
        `,
    },
    downloadTemplateButton: {
        css: css`
            ${buttonStyles()}
            margin-bottom: ${pxToRem(25)};
        `,
        variant: "primary",
    },
    fileGridItem: {
        width: 12,
    },
    nameGridItem: {
        width: 12,
    },
    descriptionGridItem: {
        width: 12,
    },
    buttonsGridItem: {
        width: 12,
    },
    cancelButton: {
        variant: "secondary",
    },
    createButton: {
        css: css`
            margin-right: ${pxToRem(10)};
        `,
    },
    includeFirstRowCheckbox: {
        css: css`
            display: block;
            margin: ${pxToRem(15)} 0 0 0;

            ${breakpoint("xs", "only")} {
                margin: ${pxToRem(25)} 0;
            }
        `,
    },
    fileWrapper: {
        css: css`
            width: 100%;
        `,
    },
    importListFormWrapper: {
        css: css`
            width: 100%;
        `,
    },
};

const styles = storeListsImportListFormStyles;

const StoreListsImportListForm: FC<Props> = ({
    uploadedItems,
    uploadLimitExceeded,
    isBadFile,
    onSubmit,
    onCancel,
    onImportError,
    storeListProcessFile,
    setIsBadFile,
    setUploadLimitExceeded,
    importStoreList,
    validateImportStoreList,
    listNameErrorMessage,
}) => {
    const toasterContext = useContext(ToasterContext);
    const fileUploadRef = useRef({ value: "" } as HTMLInputElement);

    const [name, setName] = useState<string>("");
    const [nameError, setNameError] = useState<React.ReactNode>(null);
    const [description, setDescription] = useState<string>("");
    const [descriptionError, setDescriptionError] = useState<React.ReactNode>(null);
    const [file, setFile] = useState<any>(null);
    const [fileError, setFileError] = useState<React.ReactNode>(null);
    const [incorrectFileExtension, setIncorrectFileExtension] = React.useState(false);
    const [firstRowHeading, setFirstRowHeading] = React.useState(true);
    const firstRowHeadingChangeHandler: CheckboxProps["onChange"] = (_, value) => {
        setFirstRowHeading(value);
    };

    React.useEffect(() => manageUploadedItems(), [uploadedItems]);

    React.useEffect(() => {
        if (listNameErrorMessage) {
            setNameError(listNameErrorMessage);
        }
    }, [listNameErrorMessage]);

    React.useEffect(() => {
        setFileError(null);
        if (file === null) {
            setIsBadFile({ isBadFile: false });
        }
    }, [file]);

    const submitHandler = (e: any) => {
        if (file === null) {
            setFileError(translate("Field is required"));
        }

        if (name === "") {
            setNameError(siteMessage("Lists_List_Name_Required"));
        }

        if (fileError || nameError) {
            return;
        }

        if (file) {
            const reader = new FileReader();
            reader.onload = onReaderLoad(getFileExtension(file.name));
            reader.readAsArrayBuffer(file);
        }
    };

    const onReaderLoad = (fileExtension: string) => {
        return (e: Event) => {
            const data = (e.target as any).result;
            storeListProcessFile({ data, fileExtension, firstRowHeading });
        };
    };

    const onSubmitSuccess = () => {
        toasterContext.addToast({
            body: translate("Store List Imported"),
            messageType: "success",
        });
        onSubmit();
    };

    const onSubmitError = (errorMessage: string) => {
        setNameError(errorMessage);
    };

    const onValidateError = (errorMessage: string) => {};

    const nameChangeHandler = (e: any) => {
        setName(e.target.value);
        setNameError(e.target.value.length > 100 ? siteMessage("Lists_List_Name_Too_Long", "100") : "");
    };

    const descriptionChangeHandler = (e: any) => {
        setDescription(e.target.value);
        setDescriptionError(e.target.value.length > 300 ? siteMessage("Lists_List_Description_Too_Long", "300") : "");
    };

    const fileChangeHandler = (event: React.ChangeEvent<any>) => {
        const uploadedFile = event.target.files[0];
        setFile(uploadedFile);
        if (uploadedFile) {
            const fileExtension = getFileExtension(uploadedFile.name);
            const incorrectFileExtension = ["xls", "xlsx"].indexOf(fileExtension) === -1;
            setIncorrectFileExtension(incorrectFileExtension);
            setIsBadFile({ isBadFile: incorrectFileExtension });
            setUploadLimitExceeded({ uploadLimitExceeded: false });
        }
    };

    const downloadTemplateHandler = () => {
        window.open(`${window.location.origin}${constants.path.staticFiles}/excel/StoreListUploadTemplate.xlsx`);
    };

    const manageUploadedItems = () => {
        if (!file) {
            return;
        }

        if (uploadedItems.length === 0) {
            setIsBadFile({ isBadFile: true });
            return;
        }

        if (uploadedItems.length > 0 && name) {
            validateImportStoreList({
                apiParameter: {
                    uploadedItems,
                    storeListName: name,
                    storeListDescription: description,
                },
                onSuccess: result => {
                    if (result.importLineErrors && result.importLineErrors.length > 0) {
                        const canContinueImport = uploadedItems.length !== result.importLineErrors.length;
                        onImportError(result.importLineErrors, canContinueImport, uploadedItems, name, description);
                    } else {
                        importStoreList({
                            apiParameter: {
                                uploadedItems,
                                storeListName: name,
                                storeListDescription: description,
                            },
                            onSuccess: onSubmitSuccess,
                            onError: onSubmitError,
                            onComplete(resultProps) {
                                if (resultProps.result?.storeList) {
                                    this.onSuccess?.();
                                } else if (resultProps.result?.errorMessage) {
                                    this.onError?.(resultProps.result.errorMessage);
                                }
                            },
                        });
                    }
                },
                onError: onValidateError,
                onComplete(resultProps) {},
            });
        }
    };

    return (
        <StyledWrapper {...styles.importListFormWrapper}>
            <StyledWrapper>
                <Typography as="p" {...styles.importTooltipText}>
                    {siteMessage("StoreListImport_UploadLinkTooltip")}
                </Typography>
                <Button {...styles.downloadTemplateButton} onClick={downloadTemplateHandler}>
                    {translate("Download Template")}
                </Button>
            </StyledWrapper>
            <GridContainer data-test-selector="importListForm">
                <GridItem {...styles.fileGridItem}>
                    <StyledWrapper {...styles.fileWrapper}>
                        <FileUpload
                            inputRef={fileUploadRef}
                            fileName={file?.name || ""}
                            accept=".xls,.xlsx"
                            label={translate("Upload a File")}
                            labelPosition="top"
                            error={
                                fileError ||
                                (incorrectFileExtension
                                    ? translate("must be .xls, or .xlsx")
                                    : uploadLimitExceeded
                                    ? siteMessage("ListUpload_RowsLimitExceeded")
                                    : isBadFile
                                    ? siteMessage("ListUpload_UploadError")
                                    : undefined)
                            }
                            onFileChange={fileChangeHandler}
                            uid="importStoreListFileUpload"
                            placeholder={translate("Choose File")}
                            required
                        />
                        <Checkbox
                            {...styles.includeFirstRowCheckbox}
                            onChange={firstRowHeadingChangeHandler}
                            checked={firstRowHeading}
                            uid="includeHeading"
                            data-test-selector="orderUpload_includeHeading"
                        >
                            {translate("Include First Row Column Heading")}
                        </Checkbox>
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.nameGridItem}>
                    <TextField
                        data-test-selector="storeListsImportListFormName"
                        label={translate("List Name")}
                        name="name"
                        error={nameError}
                        required
                        defaultValue={name}
                        onChange={nameChangeHandler}
                    ></TextField>
                </GridItem>
                <GridItem {...styles.descriptionGridItem}>
                    <TextField
                        data-test-selector="storeListsImportListFormDescription"
                        label={translate("Description")}
                        name="description"
                        error={descriptionError}
                        defaultValue={description}
                        onChange={descriptionChangeHandler}
                    ></TextField>
                </GridItem>
                <GridItem {...styles.buttonsGridItem}>
                    <Button
                        {...styles.createButton}
                        onClick={submitHandler}
                        disabled={name.length > 100 || description.length > 300}
                        data-test-selector="storeListsImportListFormSubmit"
                    >
                        {translate("Import List")}
                    </Button>
                    <Button {...styles.cancelButton} onClick={onCancel}>
                        {translate("Cancel")}
                    </Button>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(StoreListsImportListForm);
